import React, { useState } from "react";
import { ShieldCheck, Users2, Clock, Flag, Ban, Star, Plus, X, Sparkles } from "lucide-react";
import { ccInput, ccLabel, ccCard, ccBtnPink, ccBtnGhost, Avatar } from "./ui";

export const AdminPanel = ({ ctx }) => {
  const [newCategory, setNewCategory] = useState("");
  const active = ctx.caregivers.filter((c) => c.status === "active");
  const pending = ctx.caregivers.filter((c) => c.status === "pending");
  const suspended = ctx.caregivers.filter((c) => c.status === "suspended");
  const openReports = ctx.reports.filter((r) => r.status === "open");

  const stats = [
    { label: "Active Caregivers", value: active.length, icon: Users2, wrap: "bg-[#E2F4E1]", color: "text-[#7CC98F]", testId: "cc-admin-active" },
    { label: "Pending Verification", value: pending.length, icon: Clock, wrap: "bg-[#FFF2D1]", color: "text-[#D9A93E]", testId: "cc-admin-pending" },
    { label: "Reported Profiles", value: openReports.length, icon: Flag, wrap: "bg-rose-50", color: "text-rose-400", testId: "cc-admin-reported" },
    { label: "Suspended", value: suspended.length, icon: Ban, wrap: "bg-[#FCE6E9]", color: "text-[#E5A0BC]", testId: "cc-admin-suspended" },
    { label: "Connections", value: ctx.connections.length, icon: ShieldCheck, wrap: "bg-[#E6F2FA]", color: "text-[#5CB3DC]", testId: "cc-admin-connections" },
    { label: "Reviews", value: ctx.reviews.length, icon: Star, wrap: "bg-[#FFF2D1]", color: "text-[#F5C445]", testId: "cc-admin-reviews" },
  ];

  return (
    <div className="space-y-8" data-testid="cc-admin">
      <div>
        <h2 className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100">Care Connections Admin</h2>
        <p className="text-xs text-[#9CA3AF] font-medium mt-1">
          Visible to admin-role profiles only (Michael Winston + invited administrators).
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="cc-admin-stats">
        {stats.map((s) => (
          <div key={s.testId} className={`${ccCard} p-4`} data-testid={s.testId}>
            <span className={`w-9 h-9 rounded-full ${s.wrap} flex items-center justify-center mb-3`}>
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </span>
            <p className="text-2xl font-black font-heading text-[#374151] dark:text-slate-100">{s.value}</p>
            <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wide">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Caregiver moderation */}
      <div className={`${ccCard} p-6`}>
        <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-4">Caregiver Network</h3>
        <div className="space-y-3" data-testid="cc-admin-caregivers">
          {ctx.caregivers.map((c) => (
            <div key={c.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 flex-wrap dark:bg-[#273244] dark:border-[#374457]" data-testid={`cc-admin-row-${c.id}`}>
              <div className="flex items-center gap-3">
                <Avatar src={c.photo} name={c.name} />
                <div>
                  <p className="text-sm font-extrabold text-[#374151] dark:text-slate-100 flex items-center gap-2">
                    {c.name}
                    {c.featured && <Sparkles className="w-3.5 h-3.5 text-[#F5C445]" />}
                  </p>
                  <p className="text-[11px] text-[#9CA3AF]">
                    {(c.categories || []).join(", ") || c.title} · {c.city}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full ${
                    c.status === "active"
                      ? "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]"
                      : c.status === "pending"
                      ? "bg-[#FFF2D1] border-[#F5E3B3] text-[#7A5A17]"
                      : "bg-rose-50 border-rose-200 text-rose-500"
                  }`}
                  data-testid={`cc-admin-status-${c.id}`}
                >
                  {c.status}
                </span>
                {c.status !== "active" && (
                  <button onClick={() => ctx.adminSetStatus(c.id, "active")} className="px-3 py-1.5 bg-[#E2F4E1] hover:bg-[#D2EBD1] text-[#2F6031] rounded-full text-[11px] font-bold transition-colors" data-testid={`cc-approve-${c.id}`}>
                    Approve
                  </button>
                )}
                {c.status !== "suspended" && (
                  <button onClick={() => ctx.adminSetStatus(c.id, "suspended")} className="px-3 py-1.5 bg-[#FFF2D1] hover:bg-[#FCEBC2] text-[#7A5A17] rounded-full text-[11px] font-bold transition-colors" data-testid={`cc-suspend-${c.id}`}>
                    Suspend
                  </button>
                )}
                <button onClick={() => ctx.toggleFeatured(c.id)} className={`px-3 py-1.5 rounded-full text-[11px] font-bold transition-colors ${c.featured ? "bg-[#F5C445]/20 text-[#7A5A17]" : "bg-white border border-[#FCE6E9] text-[#9CA3AF] hover:text-[#374151] dark:bg-[#232D3D] dark:border-[#374457]"}`} data-testid={`cc-featured-${c.id}`}>
                  {c.featured ? "Unfeature" : "Feature"}
                </button>
                <button onClick={() => ctx.adminRemove(c.id)} className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-500 rounded-full text-[11px] font-bold transition-colors" data-testid={`cc-remove-${c.id}`}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Reports */}
      <div className={`${ccCard} p-6`} data-testid="cc-admin-reports">
        <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-4">Reported Profiles & Conversations</h3>
        {ctx.reports.length === 0 ? (
          <p className="text-xs text-[#9CA3AF]">No reports.</p>
        ) : (
          <div className="space-y-3">
            {ctx.reports.map((r) => {
              const cg = ctx.caregivers.find((c) => c.id === r.caregiverId);
              return (
                <div key={r.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex items-center justify-between gap-3 flex-wrap dark:bg-[#273244] dark:border-[#374457]" data-testid={`cc-report-${r.id}`}>
                  <div>
                    <p className="text-xs font-extrabold text-[#374151] dark:text-slate-100">{cg?.name || r.caregiverId}</p>
                    <p className="text-[11px] text-[#6B7280] dark:text-slate-400">{r.reason}</p>
                    <p className="text-[10px] text-[#9CA3AF]">{r.date}</p>
                  </div>
                  {r.status === "open" ? (
                    <button onClick={() => ctx.resolveReport(r.id)} className="px-3 py-1.5 bg-[#E6F2FA] hover:bg-[#D6EBF7] text-[#3D7E9E] rounded-full text-[11px] font-bold transition-colors" data-testid={`cc-report-resolve-${r.id}`}>
                      Mark Resolved
                    </button>
                  ) : (
                    <span className="px-2.5 py-1 bg-[#E2F4E1] border border-[#C4E5C2] text-[#2F6031] text-[10px] font-extrabold uppercase rounded-full">
                      Resolved
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Categories manager */}
      <div className={`${ccCard} p-6`} data-testid="cc-admin-categories">
        <h3 className="text-sm font-extrabold font-heading text-[#374151] dark:text-slate-100 mb-1">Caregiver Categories</h3>
        <p className="text-[11px] text-[#9CA3AF] mb-4">Dynamically configurable — add or remove categories without changing application code.</p>
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            placeholder="New category name"
            className={ccInput}
            data-testid="cc-category-input"
          />
          <button
            onClick={() => {
              ctx.addCategory(newCategory);
              setNewCategory("");
            }}
            className={ccBtnPink}
            data-testid="cc-category-add"
          >
            <Plus className="w-4 h-4 inline -mt-0.5" /> Add
          </button>
        </div>
        <div className="flex gap-2 flex-wrap">
          {ctx.categories.map((c) => (
            <span key={c} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#E6F2FA] border border-[#B3E0F2] text-[#3D7E9E] text-xs font-bold rounded-full" data-testid={`cc-category-${c.replace(/\W/g, "-")}`}>
              {c}
              <button onClick={() => ctx.removeCategory(c)} className="text-[#3D7E9E]/60 hover:text-rose-400 transition-colors" data-testid={`cc-category-remove-${c.replace(/\W/g, "-")}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};
